import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-ocean-blue-9 border border-ocean-blue-9 text-stone-neutral-0 hover:bg-ocean-blue-10 hover:border-ocean-blue-10 hover:text-stone-neutral-0",
                destructive: "bg-cherry-red-10 border border-cherry-red-10 text-stone-neutral-0 hover:bg-cherry-red-9 hover:border-cherry-red-9 hover:text-stone-neutral-0",
                outline: "bg-stone-neutral-1 border border-ocean-blue-9 text-ocean-blue-9 hover:bg-stone-neutral-3 hover:border-ocean-blue-9 hover:text-ocean-blue-9",
                secondary: "bg-ocean-blue-2 border border-ocean-blue-2 text-ocean-blue-9 hover:bg-ocean-blue-3 hover:border-ocean-blue-3 hover:text-ocean-blue-9",
                ghost: "border border-transparent text-ocean-blue-9 hover:bg-ocean-blue-2 hover:text-ocean-blue-9",
                link: "text-ocean-blue-9 underline-offset-4 hover:underline",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({className, variant, size, asChild = false, ...props}, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(buttonVariants({variant, size, className}))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

export {Button, buttonVariants}
